import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Award, Globe, Video, Users, Calendar, Target, Heart, Zap, MapPin, CheckCircle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import LegalNav from '../components/LegalNav';

const About = () => {
  const navigate = useNavigate();

  const artists = [
    'Cardi B',
    'Burna Boy',
    'Popcaan',
    'Dexta Daps',
    'Spice',
    'Shenseea'
  ];

  const services = [
    { icon: Video, title: 'Cross-Channel Video Advertisements', description: 'Strategic video content for all platforms' },
    { icon: Globe, title: 'Live Streaming', description: 'Professional live event broadcasting' },
    { icon: Users, title: 'Event Coverage', description: 'Comprehensive media coverage for events' },
    { icon: Award, title: 'Music Videos', description: 'Award-winning music video production' }
  ];

  const timeline = [
    { year: '2019', title: 'Company Founded', description: 'LTV Production was established in London with a vision to revolutionize video production.' },
    { year: '2020', title: 'International Expansion', description: 'Expanded operations to New York, Miami, and the Caribbean markets.' },
    { year: '2021', title: 'Live Streaming Launch', description: 'Launched LTV Live platform for professional live event broadcasting.' },
    { year: '2022', title: 'Award-Winning Work', description: 'Collaborated with Grammy-winning artists including Cardi B and Burna Boy.' },
    { year: '2023', title: 'VOD Platform', description: 'Launched LTV VOD - our video-on-demand streaming platform.' },
    { year: '2024', title: 'European Expansion', description: 'Extended our reach across Europe with partnerships and clients.' },
    { year: '2025', title: 'Platform Growth', description: 'Enhanced streaming capabilities and expanded creator partnerships.' },
    { year: '2026', title: 'Continued Growth', description: 'Serving clients worldwide with cutting-edge video production services.' }
  ];

  const values = [
    { icon: Target, title: 'Excellence', description: 'We strive for excellence in every project, no matter the size.' },
    { icon: Heart, title: 'Passion', description: 'Our team is passionate about storytelling through visual media.' },
    { icon: Zap, title: 'Innovation', description: 'We embrace new technologies and creative approaches.' },
    { icon: Users, title: 'Collaboration', description: 'We work closely with clients to bring their vision to life.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About Us | LTV Production - Award-Winning Video Production Company</title>
        <meta name="description" content="Learn about LTV Production LTD, a London-based video production company. Our history, team, values, and commitment to creating exceptional visual content." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/about" />
      </Helmet>

      <LegalNav />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About LTV Production</h1>
          <p className="text-2xl text-gray-700 mb-4">
            Creating Visual Stories That Move People
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            London's premier video production company, delivering award-winning content since 2019.
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  LTV Production LTD is an innovative media company founded in London in 2019, specializing in a variety of media services including cross-channel video advertisements, live streaming, event coverage, and music videos.
                </p>
                <p>
                  What started as a small creative studio has grown into an internationally recognized production house, with a portfolio that includes collaborations with some of the music industry's biggest names.
                </p>
                <p>
                  Our mission is to transform ideas and visions into compelling visual stories that captivate audiences worldwide. We believe video production is an art form that requires creativity, technical expertise, and a deep understanding of our clients' goals.
                </p>
              </div>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Company Facts</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold">Founded</p>
                    <p className="text-gray-600">2019 in London, UK</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold">Headquarters</p>
                    <p className="text-gray-600">Unit 112, 77 Marsh Wall</p>
                    <p className="text-gray-600">London E14 9SG, UK</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                    <Globe className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold">Global Presence</p>
                    <p className="text-gray-600">UK, USA, Caribbean, Europe</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold">Notable Clients</p>
                    <p className="text-gray-600">Grammy-winning artists</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12">Our Journey</h2>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-black h-full hidden md:block"></div>
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                      <p className="text-2xl font-bold text-purple-600 mb-2">{item.year}</p>
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex w-4 h-4 bg-black rounded-full z-10"></div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Our Values</h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            The principles that guide everything we do at LTV Production
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="text-center border-2 border-gray-200 hover:border-black transition-colors">
                  <CardContent className="pt-8 pb-6">
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">Comprehensive media solutions for your brand</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-black transition-colors duration-300">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                        <p className="text-gray-600">{service.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Notable Artists */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Award-Winning Portfolio</h2>
            <p className="text-xl text-gray-600 mb-8">
              We've had the privilege of working with some of the industry's most talented artists
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {artists.map((artist, index) => (
              <div
                key={index}
                className="bg-gray-50 p-6 rounded-lg text-center border-2 border-gray-200 hover:border-black transition-colors duration-300"
              >
                <p className="text-lg font-bold">{artist}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* CEO Photo */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/cfe7j3vr_8C494A28-7E78-4A27-B2A8-9AA9EF9D9CBD_edited.jpg.webp"
                  alt="Christopher Edwards - CEO"
                  className="w-full rounded-lg shadow-2xl"
                />
              </div>
            </div>

            {/* CEO Content */}
            <div className="order-1 lg:order-2">
              <p className="text-purple-400 font-semibold mb-2">Leadership</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Christopher Edwards</h2>
              <p className="text-2xl text-gray-300 mb-8">Founder & Chief Executive Officer</p>
              
              <div className="space-y-4 text-lg text-gray-300 leading-relaxed">
                <p>
                  Christopher Edwards is the visionary founder and CEO of LTV Production LTD. His passion for video production began early in his career, driving him to establish one of London's most respected media companies.
                </p>
                <p>
                  Under his leadership, LTV Production has grown from a small creative studio to an international production house with clients across four continents. Christopher believes that video production is an art form that requires the ability to translate ideas and vision into compelling stories on screen.
                </p>
                <p>
                  His hands-on approach and dedication to quality have earned LTV Production collaborations with Grammy-winning artists and international brands. Christopher and his team have gained exposure in New York, Miami, Europe, and the Caribbean, strategically empowering brands through video.
                </p>
                <p>
                  Christopher welcomes potential clients to a free consultation, understanding that every great project starts with a conversation.
                </p>
              </div>

              <div className="mt-8">
                <p className="text-gray-400 mb-4">Connect with Christopher:</p>
                <a
                  href="https://instagram.com/chriscameraman"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-300"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  @chriscameraman
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Work Together?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's create something extraordinary. Contact us for a free consultation and discover how we can bring your vision to life.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => navigate('/contact')}
              className="bg-black hover:bg-gray-800 text-white px-8"
              size="lg"
            >
              Contact Us
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 border-black text-black hover:bg-gray-100 px-8"
              size="lg"
            >
              View Our Work
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
